import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	LoanApplicationListItem,
	LoanApplicationListFilters,
	LoanApplicationListPagination,
	DecisionOutcome,
	ApplicationStatus,
	SortByField,
	SortOrder
} from './types';
import { apiFetch } from './lib/api';

interface LoanApplicationsListProps {
	baseUrl: string;
}

const DEFAULT_FILTERS: LoanApplicationListFilters = {
	page: 1,
	limit: 10,
	sortBy: 'createdAt',
	sortOrder: 'desc',
};

const getOutcomeColor = (outcome: string): string => {
	switch (outcome) {
		case 'APPROVED':
			return 'bg-green-100 text-green-800';
		case 'REJECTED':
			return 'bg-red-100 text-red-800';
		case 'APPROVED_WITH_DEVIATION':
			return 'bg-yellow-100 text-yellow-800';
		case 'PROCESSING':
			return 'bg-blue-100 text-blue-800';
		default:
			return 'bg-gray-100 text-gray-800';
	}
};

const getStatusColor = (status: string): string => {
	switch (status) {
		case 'DRAFT':
			return 'bg-orange-100 text-orange-800';
		case 'INITIAL':
			return 'bg-blue-100 text-blue-800';
		case 'FINAL':
			return 'bg-green-100 text-green-800';
		default:
			return 'bg-gray-100 text-gray-800';
	}
};

const formatCurrency = (amount: number): string => {
	return new Intl.NumberFormat('en-SA', { style: 'currency', currency: 'SAR', minimumFractionDigits: 2 }).format(
		amount
	);
};

const formatDate = (dateStr: string): string => {
	return new Date(dateStr).toLocaleString('en-SA', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit'
	});
};

const LoanApplicationsList: React.FC<LoanApplicationsListProps> = ({ baseUrl }) => {
	const navigate = useNavigate();
	const [items, setItems] = useState<LoanApplicationListItem[]>([]);
	const [pagination, setPagination] = useState<LoanApplicationListPagination | null>(null);
	const [filters, setFilters] = useState<LoanApplicationListFilters>(DEFAULT_FILTERS);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');

	// Filter panel state
	const [showFilters, setShowFilters] = useState(false);
	const [filterApplicationId, setFilterApplicationId] = useState('');
	const [filterOutcomeStatus, setFilterOutcomeStatus] = useState<DecisionOutcome | ''>('');
	const [filterApplicationStatus, setFilterApplicationStatus] = useState<ApplicationStatus>('ALL');
	const [filterStartDate, setFilterStartDate] = useState('');
	const [filterEndDate, setFilterEndDate] = useState('');

	const fetchApplications = useCallback(
		async (currentFilters: LoanApplicationListFilters) => {
			setIsLoading(true);
			setError('');
			try {
				const params = new URLSearchParams();
				params.set('page', String(currentFilters.page));
				params.set('limit', String(currentFilters.limit));
				params.set('sortBy', currentFilters.sortBy);
				params.set('sortOrder', currentFilters.sortOrder);

				if (currentFilters.applicationId) params.set('applicationId', currentFilters.applicationId);
				if (currentFilters.outcomeStatus) params.set('outcomeStatus', currentFilters.outcomeStatus);
				if (currentFilters.applicationStatus && currentFilters.applicationStatus !== 'ALL')
					params.set('applicationStatus', currentFilters.applicationStatus);
				if (currentFilters.startDate) params.set('startDate', currentFilters.startDate);
				if (currentFilters.endDate) params.set('endDate', currentFilters.endDate);
				if (currentFilters.groupBy) params.set('groupBy', currentFilters.groupBy);

				const response = await apiFetch(`${baseUrl}/api/v1/application/list?${params.toString()}`);
				if (!response.ok) throw new Error(`Failed to fetch applications: ${response.status}`);

				const data = await response.json();
				if (data.success) {
					setItems(data.data.items);
					setPagination(data.data.pagination);
				} else {
					throw new Error(data.message || 'Failed to fetch applications');
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : 'An error occurred');
				setItems([]);
				setPagination(null);
			} finally {
				setIsLoading(false);
			}
		},
		[baseUrl]
	);

	useEffect(() => {
		fetchApplications(filters);
	}, [filters, fetchApplications]);

	const handleSort = (field: SortByField) => {
		setFilters((prev) => ({
			...prev,
			page: 1,
			sortBy: field,
			sortOrder: prev.sortBy === field && prev.sortOrder === 'desc' ? 'asc' : 'desc'
		}));
	};

	const handlePageChange = (newPage: number) => {
		setFilters((prev) => ({ ...prev, page: newPage }));
	};

	const handleApplyFilters = () => {
		setFilters((prev) => ({
			...prev,
			page: 1,
			applicationId: filterApplicationId || undefined,
			outcomeStatus: (filterOutcomeStatus as DecisionOutcome) || undefined,
			applicationStatus: filterApplicationStatus || undefined,
			startDate: filterStartDate ? new Date(filterStartDate).toISOString() : undefined,
			endDate: filterEndDate ? new Date(filterEndDate).toISOString() : undefined
		}));
	};

	const handleResetFilters = () => {
		setFilterApplicationId('');
		setFilterOutcomeStatus('');
		setFilterApplicationStatus('ALL');
		setFilterStartDate('');
		setFilterEndDate('');
		setFilters(DEFAULT_FILTERS);
	};

	const handleLimitChange = (newLimit: number) => {
		setFilters((prev) => ({ ...prev, page: 1, limit: newLimit }));
	};

	const getSortIcon = (field: SortByField) => {
		if (filters.sortBy !== field) return '↕';
		return filters.sortOrder === 'asc' ? '↑' : '↓';
	};

	return (
		<div className="space-y-4">
			{/* Toolbar */}
			<div className="bg-white rounded-lg shadow-sm border p-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<h3 className="text-lg font-semibold text-gray-900">Loan Applications</h3>
						{pagination && (
							<span className="text-sm text-gray-500">
								{pagination.total} total application{pagination.total !== 1 ? 's' : ''}
							</span>
						)}
					</div>
					<div className="flex items-center gap-2">
						<button
							onClick={() => fetchApplications(filters)}
							disabled={isLoading}
							className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors">
							{isLoading ? 'Loading...' : 'Refresh'}
						</button>
						<button
							onClick={() => setShowFilters(!showFilters)}
							className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
								showFilters
									? 'bg-blue-100 text-blue-700 border border-blue-300'
									: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
							}`}>
							Filters {showFilters ? '▲' : '▼'}
						</button>
					</div>
				</div>

				{/* Filters Panel */}
				{showFilters && (
					<div className="mt-4 pt-4 border-t border-gray-200">
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							{/* Application ID */}
							<div>
								<label className="block text-xs font-medium text-gray-600 mb-1">Application ID</label>
								<input
									type="text"
									value={filterApplicationId}
									onChange={(e) => setFilterApplicationId(e.target.value)}
									placeholder="Enter application ID"
									className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
								/>
							</div>

							{/* Outcome Status */}
							<div>
								<label className="block text-xs font-medium text-gray-600 mb-1">Decision Outcome</label>
								<select
									value={filterOutcomeStatus}
									onChange={(e) => setFilterOutcomeStatus(e.target.value as DecisionOutcome | '')}
									className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
									<option value="">All</option>
									<option value="APPROVED">Approved</option>
									<option value="REJECTED">Rejected</option>
									<option value="APPROVED_WITH_DEVIATION">Approved with Deviation</option>
									<option value="PROCESSING">Processing</option>
								</select>
							</div>

							{/* Application Status */}
							<div>
								<label className="block text-xs font-medium text-gray-600 mb-1">Application Status</label>
								<select
									value={filterApplicationStatus}
									onChange={(e) => setFilterApplicationStatus(e.target.value as ApplicationStatus)}
									className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
									<option value="ALL">All</option>
									<option value="INITIAL">Initial</option>
									<option value="DRAFT">Draft</option>
									<option value="FINAL">Final</option>
								</select>
							</div>

							{/* Start Date */}
							<div>
								<label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
								<input
									type="datetime-local"
									value={filterStartDate}
									onChange={(e) => setFilterStartDate(e.target.value)}
									className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
								/>
							</div>

							{/* End Date */}
							<div>
								<label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
								<input
									type="datetime-local"
									value={filterEndDate}
									onChange={(e) => setFilterEndDate(e.target.value)}
									className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
								/>
							</div>

							{/* Rows per page */}
							<div>
								<label className="block text-xs font-medium text-gray-600 mb-1">Rows per page</label>
								<select
									value={filters.limit}
									onChange={(e) => handleLimitChange(Number(e.target.value))}
									className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
									<option value={5}>5</option>
									<option value={10}>10</option>
									<option value={25}>25</option>
									<option value={50}>50</option>
									<option value={100}>100</option>
								</select>
							</div>
						</div>

						<div className="flex gap-2 mt-4">
							<button
								onClick={handleApplyFilters}
								className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
								Apply Filters
							</button>
							<button
								onClick={handleResetFilters}
								className="px-4 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 border border-gray-300 transition-colors">
								Reset
							</button>
						</div>
					</div>
				)}
			</div>

			{/* Error */}
			{error && (
				<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
					<strong>Error:</strong> {error}
				</div>
			)}

			{/* Table */}
			<div className="bg-white rounded-lg shadow-sm border overflow-hidden">
				<div className="overflow-x-auto">
					<table className="min-w-full text-sm text-left">
						<thead className="bg-gray-50 text-gray-600 uppercase text-xs font-medium border-b">
							<tr>
								<th className="px-4 py-3">Application ID</th>
								<th className="px-4 py-3">Main Applicant</th>
								<th className="px-4 py-3">Customer ID</th>
								<th
									className="px-4 py-3 cursor-pointer hover:bg-gray-100 select-none"
									onClick={() => handleSort('outcomeStatus')}>
									Decision Outcome {getSortIcon('outcomeStatus')}
								</th>
								<th
									className="px-4 py-3 cursor-pointer hover:bg-gray-100 select-none"
									onClick={() => handleSort('status')}>
									Status {getSortIcon('status')}
								</th>
								<th
									className="px-4 py-3 cursor-pointer hover:bg-gray-100 select-none"
									onClick={() => handleSort('dealAmount')}>
									Deal Amount {getSortIcon('dealAmount')}
								</th>
								<th className="px-4 py-3">Maturity</th>
								<th className="px-4 py-3">Co-Applicants</th>
								<th
									className="px-4 py-3 cursor-pointer hover:bg-gray-100 select-none"
									onClick={() => handleSort('createdAt')}>
									Decision Date {getSortIcon('createdAt')}
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-100">
							{isLoading && items.length === 0 ? (
								<tr>
									<td colSpan={9} className="px-4 py-12 text-center text-gray-500">
										<div className="flex flex-col items-center gap-2">
											<svg
												className="animate-spin h-6 w-6 text-blue-600"
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24">
												<circle
													className="opacity-25"
													cx="12"
													cy="12"
													r="10"
													stroke="currentColor"
													strokeWidth="4"
												/>
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												/>
											</svg>
											<span>Loading applications...</span>
										</div>
									</td>
								</tr>
							) : items.length === 0 ? (
								<tr>
									<td colSpan={9} className="px-4 py-12 text-center text-gray-500">
										No applications found
									</td>
								</tr>
							) : (
								items.map((item) => (
									<tr
										key={item.applicationId}
										className="hover:bg-blue-50 transition-colors cursor-pointer"
										onClick={() => navigate(`/loan-applications/${item.applicationId}`)}
										title="Click to view application details">
										<td className="px-4 py-3">
											<span className="font-mono text-xs text-blue-600 underline" title={item.applicationId}>
												{item.applicationId.slice(0, 8)}...
											</span>
										</td>
										<td className="px-4 py-3 font-medium text-gray-900">
											{item.mainApplicant.name}
										</td>
										<td className="px-4 py-3 text-gray-600 font-mono text-xs">
											{item.mainApplicant.customerId}
										</td>
										<td className="px-4 py-3">
											<span
												className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getOutcomeColor(item.decisionOutcome)}`}>
												{item.decisionOutcome}
											</span>
										</td>
										<td className="px-4 py-3">
											<span
												className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.applicationStatus)}`}>
												{item.applicationStatus}
											</span>
										</td>
										<td className="px-4 py-3 text-right font-mono">
											{formatCurrency(item.dealAmount)}
										</td>
										<td className="px-4 py-3 text-center">{item.maturity} mo</td>
										<td className="px-4 py-3 text-center">{item.coApplicants.length}</td>
										<td className="px-4 py-3 text-gray-600 text-xs">
											{formatDate(item.decisionDate)}
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>

				{/* Pagination */}
				{pagination && pagination.totalPages > 0 && (
					<div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
						<div className="text-sm text-gray-600">
							Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
							{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
						</div>
						<div className="flex items-center gap-1">
							<button
								onClick={() => handlePageChange(1)}
								disabled={pagination.page === 1 || isLoading}
								className="px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
								First
							</button>
							<button
								onClick={() => handlePageChange(pagination.page - 1)}
								disabled={pagination.page === 1 || isLoading}
								className="px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
								Prev
							</button>
							{Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
								.filter(
									(p) =>
										p === 1 ||
										p === pagination.totalPages ||
										Math.abs(p - pagination.page) <= 2
								)
								.reduce<(number | string)[]>((acc, p, i, arr) => {
									if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('...');
									acc.push(p);
									return acc;
								}, [])
								.map((p, i) =>
									typeof p === 'string' ? (
										<span key={`ellipsis-${i}`} className="px-2 py-1 text-sm text-gray-400">
											...
										</span>
									) : (
										<button
											key={p}
											onClick={() => handlePageChange(p)}
											disabled={isLoading}
											className={`px-2.5 py-1 text-sm rounded border transition-colors ${
												p === pagination.page
													? 'bg-blue-600 text-white border-blue-600'
													: 'border-gray-300 hover:bg-gray-100'
											}`}>
											{p}
										</button>
									)
								)}
							<button
								onClick={() => handlePageChange(pagination.page + 1)}
								disabled={pagination.page === pagination.totalPages || isLoading}
								className="px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
								Next
							</button>
							<button
								onClick={() => handlePageChange(pagination.totalPages)}
								disabled={pagination.page === pagination.totalPages || isLoading}
								className="px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
								Last
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default LoanApplicationsList;
